import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {CommentsIcon} from '../../tripboard/comments';
import debounce from 'lodash.debounce';
import edap from '../../tripboard/edap';
import Votes from '../../tripboard/votes/votes';
import HitBody from './HitSubComponents/HitBody/HitBody.js';
import {ImageCarousel} from '../image-carousel';
import HitDeleteButton from './HitSubComponents/HitDeleteButton/HitDeleteButton.js';
import HeartDeleteButton from './HitSubComponents/HeartDeleteButton/HeartDeleteButton.js';
import HitSpecialOffer from './HitSubComponents/HitSpecialOffer/HitSpecialOffer.js';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import {edapTriggerPriceSummaryDisplay} from '@homeaway/react-price-summary';
const HitThumbnails = require('./HitSubComponents/HitThumbnails/HitThumbnails.js').default;
import {LoggedIn} from '../auth';
import {log} from '../../utils/logger';
import Permissions from '../permissions';
import {ROLES} from '../../utils/constants';
import TripboardButtonContainer from './HitSubComponents/TripboardButtonContainer/TripboardButtonContainer';
import {UnavailableLabel} from '../../tripboard/unavailable-label/';

import './Hit.less';

export const logDelisted = debounce(log, 500, {
    leading: false,
    trailing: true
});

export function Hit(props) {
    const {
        addedTime,
        arrivalDate,
        availableForDates,
        averagePrice,
        averageRating,
        baseLocation,
        bathrooms,
        bedrooms,
        boardId,
        changeSummary,
        detailPageUrl,
        departureDate,
        gallery,
        galleryUrl,
        hasCarousel,
        headline,
        hitTotal,
        idx,
        images,
        instantBookable,
        instantBookableMessage,
        instantBookableTooltip,
        isDated,
        isMobile,
        listingId,
        miniHit,
        minStay,
        multiUnitProperty,
        multiUnitPropertyMessage,
        noPrice,
        notifyChange,
        onClick,
        onDelete,
        popularBadge,
        popularText,
        propertyId,
        rate,
        rateUnits,
        recoHit,
        edapEventJson,
        reviewCount,
        reviews,
        role,
        priceSummary,
        scratchpadId, // No longer really a "scratchpadId" varies based on model
        siteDisplayName,
        sleeps,
        specialOffer,
        spu,
        status,
        superlative,
        showHeadline,
        stay,
        thumbnailUrl1,
        thumbnailUrl2,
        thumbnailUrl3,
        translations,
        hasDeleteButton,
        hasTripboardButton,
        isHeartRemoveTestOn,
        unit,
        tripBoardUuid
    } = props;

    let ratesUnitOfMeasure = isDated ? translations.perNight : translations.avgNight;

    if (recoHit) {
        ratesUnitOfMeasure = rateUnits;
    }

    const delisted = status === 'DELISTED';
    const unavailable = status === 'UNAVAILABLE';

    if (delisted) {
        logDelisted({message: `Delisted hit shown. ListingId: ${scratchpadId}`});
    }

    const wrapperClassName = classnames('scratchpad-hit-wrapper', {
        'has-special': !!specialOffer && (!miniHit && !gallery),
        'scratchpad-hit-wrapper-gallery': gallery,
        'scratchpad-hit-wrapper-mini': miniHit,
        'scratchpad-hit-wrapper-reco': recoHit,
        'dates-unavailable': unavailable,
        'is-delisted': delisted
    });

    const mainClassName = classnames(
        'card',
        'hit-scratchpad',
        'hit-child-grid',
        `listing-spu-${spu}`,
        `preview-listing-${spu}`,
        'hit-has-collaborate',
        'hit-scratchpad__collaborate',
        {
            'hit-scratchpad-gallery card--xs': gallery,
            'hit-scratchpad-mini card--xxs': miniHit,
            'hit-scratchpad-reco card--xxs': recoHit,
            'card--no-scale': true
        }
    );

    const target = isMobile ? '_self' : '_blank';

    function onHitClick() {
        if (onClick && !delisted) {
            // if price summary edapEventJson is available then log a "price.summary.display"
            if (edapEventJson) {
                const options = {delayProcessing: true};
                edapTriggerPriceSummaryDisplay({edapEventJson}, options);
            }

            onClick(isMobile);
        }
    }

    function onCarouselArrowClick(direction) {
        edap.carouselHitArrowClick(direction);
    }

    function onCarouselHitClick(position, total) {
        edap.carouselHitClick(position, total);
        onHitClick();
        if (!delisted) {
            window.open(detailPageUrl, target);
        }
    }

    const showCarousel = hasCarousel && images.length > 1;

    const body = (
        <HitBody
            arrivalDate={arrivalDate}
            averageRating={averageRating}
            baseLocation={baseLocation}
            bathrooms={bathrooms}
            bedrooms={bedrooms}
            changeSummary={changeSummary}
            currencyUnits={averagePrice && averagePrice.currencyUnits}
            departureDate={departureDate}
            detailPageUrl={detailPageUrl}
            gallery={gallery}
            headline={headline}
            instantBookable={instantBookable}
            instantBookableMessage={instantBookableMessage}
            instantBookableTooltip={instantBookableTooltip}
            miniHit={miniHit}
            minStay={minStay}
            multiUnitProperty={multiUnitProperty}
            multiUnitPropertyMessage={multiUnitPropertyMessage}
            noPrice={noPrice}
            notifyChange={notifyChange}
            onDelete={onDelete}
            popularBadge={popularBadge}
            popularText={popularText}
            propertyId={propertyId}
            rate={rate}
            recoHit={recoHit}
            ratesUnitOfMeasure={ratesUnitOfMeasure}
            edapEventJson={edapEventJson}
            reviewCount={reviewCount}
            reviews={reviews}
            scratchpadId={scratchpadId}
            sleeps={sleeps}
            siteDisplayName={siteDisplayName}
            specialOffer={specialOffer}
            status={status}
            superlative={superlative}
            showHeadline={showHeadline}
            unit={unit}
        />
    );
    return (
        <div className={wrapperClassName}>
            {/* eslint-disable jsx-a11y/anchor-is-valid */}
            <div className={mainClassName}>
                <a
                    className="hit-wrapper-link"
                    data-spu={spu}
                    target={target}
                    href={delisted ? null : detailPageUrl}
                    onClick={onHitClick}
                    data-wdio="tripboard-details__listing"
                >
                    {unavailable && <UnavailableLabel />}
                    {/* eslint-enable jsx-a11y/anchor-is-valid */}
                    {hasDeleteButton &&
                    !isHeartRemoveTestOn && (
                        <HitDeleteButton
                            onDelete={onDelete}
                            scratchpadId={scratchpadId}
                            deleteText={translations.deleteText}
                        />
                    )}
                    {hasDeleteButton &&
                    isHeartRemoveTestOn && (
                        <HeartDeleteButton
                            deleteText={translations.deleteText}
                            idx={idx}
                            hitTotal={hitTotal}
                            listingId={scratchpadId}
                            boardId={boardId}
                        />
                    )}
                    {hasTripboardButton && (
                        <TripboardButtonContainer
                            headline={headline}
                            listingId={scratchpadId}
                            rating={averageRating}
                            reviewCount={reviewCount}
                            thumbnailUrl={thumbnailUrl1}
                        />
                    )}
                    <div className="scratchpad-hit-inner-wrapper">
                        {(!showCarousel || delisted) && (
                            <HitThumbnails
                                addedTime={addedTime}
                                gallery={gallery}
                                galleryUrl={galleryUrl}
                                miniHit={miniHit}
                                recoHit={recoHit}
                                spu={spu}
                                thumbnailUrl1={thumbnailUrl1}
                                thumbnailUrl2={thumbnailUrl2 || thumbnailUrl1}
                                thumbnailUrl3={thumbnailUrl3 || thumbnailUrl1}
                            />
                        )}
                        {showCarousel &&
                        !delisted && (
                            <ImageCarousel
                                images={images}
                                isMobile={isMobile}
                                onCarouselArrowClick={onCarouselArrowClick}
                                onCarouselHitClick={onCarouselHitClick}
                                slideSize={'cover'}
                            />
                        )}
                        {body}
                    </div>
                </a>
                <LoggedIn>
                    <Permissions role={role} owner collaborator>
                        <div className="flex flex-space-between hit-collaborate">
                            <Votes
                                className="hit-votes"
                                endDate={departureDate}
                                listingId={listingId}
                                startDate={arrivalDate}
                                tripBoardUuid={tripBoardUuid}
                                sm
                            />
                            <CommentsIcon
                                availableForDates={availableForDates}
                                averagePrice={averagePrice}
                                averageRating={averageRating}
                                baseLocation={baseLocation}
                                bathrooms={bathrooms}
                                bedrooms={bedrooms}
                                boardId={boardId}
                                className="hit-comments-icon"
                                detailPageUrl={detailPageUrl}
                                endDate={departureDate}
                                hasCarousel={hasCarousel}
                                headline={headline}
                                images={images}
                                instantBookable={instantBookable}
                                listingId={listingId}
                                priceSummary={priceSummary}
                                reviewCount={reviewCount}
                                sleeps={sleeps}
                                startDate={arrivalDate}
                                status={status}
                                stay={stay}
                            />
                        </div>
                    </Permissions>
                </LoggedIn>
            </div>
            <HitSpecialOffer specialOffer={specialOffer} />
        </div>
    );
}

Hit.propTypes = {
    addedTime: PropTypes.string,
    averageRating: PropTypes.number,
    bathrooms: PropTypes.string,
    bedrooms: PropTypes.string,
    changeSummary: PropTypes.object /* eslint react/forbid-prop-types: 0 */,
    dateNotAvailable: PropTypes.string,
    detailPageUrl: PropTypes.string.isRequired,
    gallery: PropTypes.bool,
    galleryUrl: PropTypes.string.isRequired,
    hasCarousel: PropTypes.bool,
    headline: PropTypes.string,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            c6_uri: PropTypes.string,
            c10_uri: PropTypes.string
        })
    ),
    instantBookable: PropTypes.bool,
    instantBookableMessage: PropTypes.string,
    instantBookableTooltip: PropTypes.string,
    isDated: PropTypes.bool,
    miniHit: PropTypes.bool,
    minStay: PropTypes.string,
    multiUnitProperty: PropTypes.bool,
    multiUnitPropertyMessage: PropTypes.string,
    noPrice: PropTypes.string,
    notifyChange: PropTypes.bool,
    onClick: PropTypes.func,
    onDelete: PropTypes.func,
    popularBadge: PropTypes.string,
    popularText: PropTypes.string,
    propertyId: PropTypes.string,
    rate: PropTypes.string,
    ratesUnitOfMeasure: PropTypes.string,
    recoHit: PropTypes.bool,
    edapEventJson: PropTypes.string,
    reviewCount: PropTypes.number,
    reviews: PropTypes.string,
    role: PropTypes.string,
    scratchpadId: PropTypes.string,
    sleeps: PropTypes.string,
    specialOffer: PropTypes.shape({
        formattedDate: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired
    }),
    spu: PropTypes.string,
    status: PropTypes.string.isRequired,
    superlative: PropTypes.string,
    showHeadline: PropTypes.bool,
    thumbnailUrl1: PropTypes.string,
    thumbnailUrl2: PropTypes.string,
    thumbnailUrl3: PropTypes.string,
    translations: PropTypes.object,
    votes: PropTypes.shape({
        voteCount: PropTypes.number,
        voteUuid: PropTypes.string,
        isMine: PropTypes.bool
    }),
    hasDeleteButton: PropTypes.bool,
    hasTripboardButton: PropTypes.bool
};

Hit.defaultProps = {
    gallery: false,
    galleryUrl: 'https://csvcus.homeaway.com/rsrcs/cdn-logos/2.6.1/bce/brand/misc/property-image-placeholder.svg',
    hasCarousel: false,
    headline: '',
    images: [],
    isDated: false,
    onClick: () => {},
    miniHit: false,
    recoHit: false,
    role: ROLES.VIEWER,
    showHeadline: false,
    smallImageTestValue: 0,
    spu: '',
    translations: {},
    hasDeleteButton: true,
    hasTripboardButton: false
};

export default determineIfMobile(Hit);



// WEBPACK FOOTER //
// ./src/universal/components/Hit/Hit.js